<template>
  <nav class="sidebar" :class="{ 'sidebar--collapsed': collapsed }">
    <div class="sidebar-head">
      <div v-if="!collapsed" class="sidebar-brand">
        <img :src="logoUrl" alt="ISBET" class="sidebar-brand-img" />
      </div>
      <button
        class="sidebar-toggle"
        @click="$emit('update:collapsed', !collapsed)"
        :aria-label="collapsed ? 'Expandir menu' : 'Recolher menu'"
        :data-tooltip="collapsed ? 'Expandir menu' : 'Recolher menu'"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
    </div>

    <div class="sidebar-nav">
      <button
        class="nav-item nav-item--search"
        @click="openGlobalSearch"
        :aria-label="collapsed ? 'Buscar (Ctrl+K)' : undefined"
        data-tooltip="Buscar · Ctrl+K"
      >
        <span class="nav-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </span>
        <span v-if="!collapsed" class="nav-label">Buscar</span>
        <kbd v-if="!collapsed" class="nav-kbd">Ctrl K</kbd>
      </button>

      <router-link
        v-for="item in topItems"
        :key="item.to"
        :to="item.to"
        class="nav-item"
        :exact-active-class="item.exact ? 'nav-item--active' : undefined"
        :active-class="item.exact ? undefined : 'nav-item--active'"
        :aria-label="collapsed ? item.label : undefined"
        :data-tooltip="item.label"
      >
        <span class="nav-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="item.icon"></svg>
        </span>
        <span v-if="!collapsed" class="nav-label">{{ item.label }}</span>
      </router-link>

      <!-- Grupos: expandido = acordeão (abre sozinho o grupo da página atual); recolhido = painel
           flutuante ao passar o mouse. Sem isso a lista inteira não cabia em telas mais baixas. -->
      <div
        v-for="group in visibleGroups"
        :key="group.key"
        class="nav-group"
        :class="{ 'nav-group--open': isOpen(group.key), 'nav-group--active': groupActive(group) }"
      >
        <button
          type="button"
          class="nav-item nav-group-head"
          :aria-expanded="isOpen(group.key)"
          :aria-label="collapsed ? group.label : undefined"
          @click="toggleGroup(group.key)"
        >
          <span class="nav-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="group.icon"></svg>
          </span>
          <span v-if="!collapsed" class="nav-label">{{ group.label }}</span>
          <svg v-if="!collapsed" class="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <div v-if="collapsed || isOpen(group.key)" class="nav-sub" :class="{ 'nav-sub--flyout': collapsed }">
          <div v-if="collapsed" class="nav-flyout-title">{{ group.label }}</div>
          <template v-for="item in group.visibleItems" :key="item.to">
            <!-- Subtítulo dentro do grupo (ex.: IA › Base de conhecimento / Aprendizado / Atalhos). -->
            <div v-if="item.heading" class="nav-sub-heading">{{ item.heading }}</div>
            <router-link
              :to="item.to"
              class="nav-item nav-subitem"
              :class="item.className"
              :active-class="item.shortcut ? '' : 'nav-item--active'"
              :exact-active-class="item.shortcut ? '' : undefined"
            >
              <span class="nav-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="item.icon"></svg>
              </span>
              <span class="nav-label">{{ item.label }}</span>
              <span
                v-if="item.count && aiMenuCounts[item.count.key] !== null"
                class="nav-count"
                :class="{ 'nav-count--alert': item.count.alert && (aiMenuCounts[item.count.key] ?? 0) > 0 }"
              >{{ aiMenuCounts[item.count.key] }}</span>
              <svg v-if="item.shortcut" class="nav-external" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </router-link>
          </template>
        </div>
      </div>
    </div>

    <div class="sidebar-shortcuts">
      <button
        class="nav-item nav-item--shortcuts"
        @click="toggleShortcutsHelp"
        :aria-label="collapsed ? 'Atalhos de teclado (?)' : undefined"
        data-tooltip="Atalhos de teclado · ?"
      >
        <span class="nav-icon nav-icon--shortcuts">?</span>
        <span v-if="!collapsed" class="nav-label">Atalhos</span>
        <kbd v-if="!collapsed" class="nav-kbd">?</kbd>
      </button>
    </div>

    <div v-if="can('admin.view')" class="sidebar-footer">
      <router-link
        to="/admin"
        class="nav-item nav-item--admin"
        active-class="nav-item--active"
        :aria-label="collapsed ? 'Admin' : undefined"
        data-tooltip="Admin"
      >
        <span class="nav-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </span>
        <span v-if="!collapsed" class="nav-label">Admin</span>
      </router-link>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePermission } from '@/composables/usePermission'
import { openGlobalSearch } from '@/composables/useGlobalSearch'
import { toggleShortcutsHelp } from '@/composables/useShortcutsHelp'
import { useLogo } from '@/composables/useLogo'
import { useStore } from 'vuex'
import { aiMenuCounts, loadAiMenuCounts, type AiMenuCountKey } from '@/composables/useAiMenuCounts'

const { logoUrl } = useLogo()

const props = defineProps<{
  collapsed: boolean
}>()

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
}>()

const route   = useRoute()
const { can } = usePermission()

interface NavItem {
  to: string; label: string; perm?: string; icon: string; exact?: boolean; className?: string
  /** Subtítulo mostrado antes do item, dentro do grupo. */
  heading?: string
  /** Contador ao lado (alert = vermelho quando > 0). */
  count?: { key: AiMenuCountKey; alert?: boolean }
  /** Atalho pra outra tela (ícone de seta, nunca fica marcado como ativo). */
  shortcut?: boolean
}
interface NavGroup { key: string; label: string; icon: string; items: NavItem[] }

const ICONS = {
  dashboard: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  foco: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/><line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/>',
  chat: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  headset: '<path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>',
  chart: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  people: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  resumo: '<rect x="2" y="3" width="5" height="5" rx="1"/><rect x="9" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="6" height="5" rx="1"/><rect x="2" y="11" width="5" height="10" rx="1"/><rect x="9" y="11" width="5" height="6" rx="1"/><rect x="16" y="11" width="6" height="8" rx="1"/>',
  check: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  bolt: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  flow: '<rect x="3" y="3" width="7" height="6" rx="1"/><rect x="14" y="15" width="7" height="6" rx="1"/><path d="M6.5 9v3a2 2 0 0 0 2 2h7a2 2 0 0 1 2 2v-1"/>',
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  cpu: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>',
  list: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
  agent: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/>',
  tag: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  robot: '<rect x="4" y="7" width="16" height="12" rx="3"/><path d="M12 3v4"/><circle cx="12" cy="3" r="1"/><circle cx="9" cy="13" r="1.2"/><circle cx="15" cy="13" r="1.2"/><path d="M9.5 16.5h5"/>',
  book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
  bulb: '<path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V17h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  alert: '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
}

const TOP_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: ICONS.dashboard, exact: true },
  { to: '/foco', label: 'Modo Foco', perm: 'foco.view', icon: ICONS.foco },
]

const GROUPS: NavGroup[] = [
  {
    key: 'atendimento', label: 'Atendimento', icon: ICONS.headset, items: [
      { to: '/fila', label: 'Fila', perm: 'fila.view', icon: ICONS.list },
      { to: '/contacts', label: 'Contatos', perm: 'contacts.view', icon: ICONS.people },
      { to: '/templates', label: 'Templates WA', perm: 'templates.view', icon: ICONS.chat },
      { to: '/macros', label: 'Macros', perm: 'macros.view', icon: ICONS.bolt },
      { to: '/fluxos-bot', label: 'Fluxos de bot', perm: 'bot.view', icon: ICONS.flow },
      { to: '/ciclos', label: 'Ciclos de envio', perm: 'ciclos.view', icon: ICONS.clock },
    ],
  },
  {
    key: 'relatorios', label: 'Relatórios', icon: ICONS.chart, items: [
      { to: '/resumo', label: 'Resumo', perm: 'resumo.view', icon: ICONS.resumo },
      { to: '/metricas', label: 'Métricas', perm: 'metricas.view', icon: ICONS.chart },
      { to: '/relatorios', label: 'Relatórios da operação', perm: 'relatorios.view', icon: ICONS.list },
      { to: '/relatorio-resolucao', label: 'Relatório de Resolução', perm: 'relatorioResolucao.view', icon: ICONS.check },
      { to: '/sla', label: 'SLA', perm: 'sla.view', icon: ICONS.clock },
      { to: '/avaliacoes', label: 'Avaliações', perm: 'avaliacoes.view', icon: ICONS.star },
      { to: '/sla-metro', label: 'SLA Telão', perm: 'slaMetro.view', icon: ICONS.alert, className: 'nav-item--metro' },
    ],
  },
  {
    key: 'gestao', label: 'Gestão', icon: ICONS.settings, items: [
      { to: '/agentes', label: 'Agentes', perm: 'agentes.view', icon: ICONS.agent },
      { to: '/teams', label: 'Times', perm: 'teams.view', icon: ICONS.people },
      { to: '/etiquetas', label: 'Etiquetas', perm: 'etiquetas.view', icon: ICONS.tag },
      { to: '/consumo', label: 'Consumo do servidor', perm: 'monitoring.view', icon: ICONS.cpu },
    ],
  },
  {
    // Inteligência Artificial: a tela que ficava no Admin, agora em /ia/<página>, com os submenus como subtítulos.
    key: 'ia', label: 'Inteligência Artificial', icon: ICONS.robot, items: [
      { to: '/ia/agentes', label: 'Agentes de IA', perm: 'ai.view', icon: ICONS.robot, count: { key: 'agents' } },
      { to: '/ia/documentos', label: 'Documentos', perm: 'ai.view', icon: ICONS.book, heading: 'Base de conhecimento', count: { key: 'documents' } },
      { to: '/ia/importar-pdf', label: 'Importar PDF', perm: 'ai.edit', icon: ICONS.upload },
      { to: '/ia/conversas', label: 'Conversas', perm: 'ai.view', icon: ICONS.chat, heading: 'Aprendizado' },
      { to: '/ia/sugestoes', label: 'Sugestões', perm: 'ai.view', icon: ICONS.bulb, count: { key: 'suggestions', alert: true } },
      { to: '/ia/duvidas-frequentes', label: 'Dúvidas frequentes', perm: 'ai.mark', icon: ICONS.help, count: { key: 'faq', alert: true } },
      { to: '/ia/o-que-sabe', label: 'O que o agente já sabe', perm: 'ai.view', icon: ICONS.check, heading: 'Conferência' },
      { to: '/admin?abrir=vertex', label: 'Vertex AI', perm: 'settings.manage', icon: ICONS.settings, heading: 'Atalhos', shortcut: true },
      { to: '/relatorios?r=ia', label: 'Consumo de IA', perm: 'relatorios.view', icon: ICONS.chart, shortcut: true },
    ],
  },
]

const allowed = (i: NavItem) => !i.perm || can(i.perm)
const topItems = computed(() => TOP_ITEMS.filter(allowed))
// Grupo sem nenhum item permitido some inteiro.
const visibleGroups = computed(() =>
  GROUPS.map(g => ({ ...g, visibleItems: g.items.filter(allowed) })).filter(g => g.visibleItems.length > 0))

const inRoute = (to: string) => route.path === to || route.path.startsWith(`${to}/`)
const groupActive = (g: NavGroup) => g.items.some(i => inRoute(i.to))

// Grupos abertos ficam salvos (só conveniência por navegador — se o storage falhar, todos começam fechados).
const STORAGE_KEY = 'sidebar_open_groups'
function readOpen(): string[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') } catch { return [] }
}
const openGroups = ref<string[]>(readOpen())
watch(openGroups, v => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)) } catch { /* sem storage */ } }, { deep: true })

const isOpen = (key: string) => openGroups.value.includes(key)

function toggleGroup(key: string) {
  // Recolhido: o clique abre o menu já com o grupo expandido (o painel flutuante é só no hover).
  if (props.collapsed) {
    if (!isOpen(key)) openGroups.value = [...openGroups.value, key]
    emit('update:collapsed', false)
    return
  }
  openGroups.value = isOpen(key) ? openGroups.value.filter(k => k !== key) : [...openGroups.value, key]
}

// Contadores da IA: carregam ao abrir o app e de novo a cada entrada numa página /ia (a tela também recarrega ao salvar).
const store = useStore()
watch(() => route.path.startsWith('/ia/'), (inIa, wasIa) => {
  if (!can('ai.view') || (wasIa !== undefined && !inIa)) return
  loadAiMenuCounts(store.getters.authUser?.wootdash_token ?? '', can('ai.mark'))
}, { immediate: true })

// Página atual dentro de um grupo fechado: abre ele, pra o item ativo ficar visível.
watch(() => route.path, () => {
  const g = GROUPS.find(groupActive)
  if (g && !isOpen(g.key)) openGroups.value = [...openGroups.value, g.key]
}, { immediate: true })
</script>

<style scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 250px;
  background: var(--night);
  z-index: 300;
  display: flex;
  flex-direction: column;
  transition: width var(--ease);
  /* visible, não hidden: o tooltip custom (::after/::before) dos nav-items e
     do sidebar-toggle é posicionado fora da caixa do sidebar (à direita do
     ícone) — com overflow:hidden aqui ele nunca aparece, fica cortado antes
     de ser pintado. Nada dentro do sidebar depende de clipping (labels somem
     via v-if, não via CSS truncation animada), então não tem risco visual. */
  overflow: visible;
  box-shadow: 2px 0 18px rgba(2, 22, 39, 0.45);
}

.sidebar--collapsed {
  width: 64px;
}

.sidebar-head {
  display: flex;
  align-items: center;
  height: 66px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  gap: 10px;
  flex-shrink: 0;
  overflow: visible; /* mesma razão do .sidebar acima — libera o tooltip do sidebar-toggle */
}

.sidebar--collapsed .sidebar-head {
  justify-content: center;
  padding: 0;
}

.sidebar-brand {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.sidebar-brand-img {
  height: 36px;
  object-fit: contain;
  max-width: 100%;
}

.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--r-sm);
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.65);
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--ease), color var(--ease);
}

.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.sidebar-nav {
  flex: 1;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: visible; /* mesma razão do .sidebar acima — libera o tooltip dos nav-items */
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--r-sm);
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  transition: background var(--ease), color var(--ease);
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.85);
}

.nav-item--active {
  background: rgba(188, 12, 36, 0.18);
  color: #fff;
}

.nav-item--active .nav-icon {
  color: var(--red);
}

.sidebar--collapsed .nav-item {
  justify-content: center;
  padding: 10px;
}

.nav-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-label {
  font-size: 13px;
  font-weight: 500;
}

.sidebar-footer {
  padding: 8px 8px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
}

.nav-item--admin {
  color: rgba(255, 255, 255, 0.45);
}
.nav-item--admin:hover {
  color: rgba(255, 255, 255, 0.75);
}

.nav-item--search {
  width: 100%;
  border: none;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  margin-bottom: 4px;
  border: 1px solid rgba(255, 255, 255, 0.07);
}
.nav-item--search:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
}

.sidebar-shortcuts {
  padding: 0 8px 4px;
  flex-shrink: 0;
}

.nav-item--shortcuts {
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.35);
}

.nav-item--shortcuts:hover {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.65);
}

.nav-icon--shortcuts {
  font-size: 14px;
  font-weight: 700;
  font-style: normal;
  width: 20px;
  height: 20px;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.nav-kbd {
  margin-left: auto;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  font-family: inherit;
  flex-shrink: 0;
}
.nav-item--metro {
  color: rgba(239, 100, 68, 0.75);
}
.nav-item--metro:hover {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
}

/* Tooltip customizado (substitui o `title` nativo do navegador) — só nos
   itens de navegação quando o menu está recolhido (ícone sem label ao lado);
   o botão de recolher/expandir mostra em ambos os estados, pois é sempre
   ícone-only. Acessibilidade fica a cargo de aria-label, que continua sempre
   presente independente do CSS. */
.sidebar-toggle[data-tooltip],
.sidebar--collapsed .nav-item[data-tooltip] {
  position: relative;
  /* .nav-item usa overflow:hidden pra truncar o label (white-space:nowrap)
     — mas no colapsado não existe label nenhum renderizado (v-if some com
     ele), então liberar aqui não trunca nada e deixa o tooltip escapar. */
  overflow: visible;
}

.sidebar-toggle[data-tooltip]:hover::after,
.sidebar-toggle[data-tooltip]:focus-visible::after,
.sidebar--collapsed .nav-item[data-tooltip]:hover::after,
.sidebar--collapsed .nav-item[data-tooltip]:focus-visible::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--night);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .2px;
  white-space: nowrap;
  padding: 7px 12px;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(2, 22, 39, .35), 0 2px 8px rgba(2, 22, 39, .25);
  z-index: 500;
  pointer-events: none;
  animation: nav-tooltip-in .14s ease-out;
}

.sidebar-toggle[data-tooltip]:hover::before,
.sidebar-toggle[data-tooltip]:focus-visible::before,
.sidebar--collapsed .nav-item[data-tooltip]:hover::before,
.sidebar--collapsed .nav-item[data-tooltip]:focus-visible::before {
  content: '';
  position: absolute;
  left: calc(100% + 5px);
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: var(--night);
  z-index: 500;
  pointer-events: none;
}

@keyframes nav-tooltip-in {
  from { opacity: 0; transform: translateY(-50%) translateX(-4px); }
  to   { opacity: 1; transform: translateY(-50%) translateX(0); }
}

/* ── Grupos expansíveis ───────────────────────────────────────────── */
/* Expandido: a lista rola se ainda assim não couber. Recolhido não rola — o painel
   flutuante e os tooltips precisam escapar da caixa (ver overflow do .sidebar). */
.sidebar:not(.sidebar--collapsed) .sidebar-nav {
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
}

.nav-group {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-group-head {
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.nav-group--active > .nav-group-head {
  color: rgba(255, 255, 255, 0.9);
}
.nav-group--active > .nav-group-head .nav-icon {
  color: var(--red);
}

.nav-chevron {
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0.6;
  transition: transform var(--ease);
}
.nav-group--open > .nav-group-head .nav-chevron {
  transform: rotate(180deg);
}

.nav-sub:not(.nav-sub--flyout) {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 21px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.nav-subitem {
  padding: 8px 10px;
  font-size: 12.5px;
}

/* Subtítulos e contadores dentro de um grupo (Inteligência Artificial) */
.nav-sub-heading {
  position: relative;
  padding: 8px 10px 2px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
}
.nav-sub--flyout .nav-sub-heading { margin: 0 6px; }
.nav-count {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
.nav-count--alert {
  background: var(--red);
  color: #fff;
}
.nav-external {
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0.5;
}
.nav-subitem .nav-label {
  font-size: 12.5px;
}

/* Recolhido: subitens num painel ao lado do ícone, aberto no hover/foco do grupo. */
.nav-sub--flyout {
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  padding-left: 12px; /* ponte invisível: o mouse atravessa sem fechar o painel */
  z-index: 500;
}
.nav-group:hover > .nav-sub--flyout,
.nav-group:focus-within > .nav-sub--flyout {
  display: flex;
  flex-direction: column;
}
.nav-sub--flyout::before {
  content: '';
  position: absolute;
  inset: 0 0 0 12px;
  background: var(--night);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(2, 22, 39, .35), 0 2px 8px rgba(2, 22, 39, .25);
  z-index: -1;
}
.nav-sub--flyout .nav-subitem {
  position: relative;
  margin: 0 6px;
  min-width: 190px;
  justify-content: flex-start;
  padding: 8px 10px;
}
.nav-sub--flyout .nav-subitem:last-child {
  margin-bottom: 6px;
}
.nav-flyout-title {
  position: relative;
  padding: 10px 16px 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}
</style>
